/**
 * ProfilePage — self-service profile: view + edit own name/phone.
 * Email, role, company are shown read-only (immutable by design server-side).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, User as UserIcon, CheckCircle2 } from 'lucide-react';
import { api, authApi } from '../utils/api';
import SignedDocumentsSection from './profile/SignedDocumentsSection';
import CertBadge from '../components/CertBadge';

export default function ProfilePage() {
  const [profile, setProfile] = useState<any>(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', phoneNumber: '' });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoVersion, setPhotoVersion] = useState(0);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    authApi.getProfile()
      .then((res: any) => {
        // API wrapper: res.data = {success, data}. getProfile returns response.data,
        // so res here IS the wrapper — unwrap to the inner payload (badge UAT F03/F08).
        const p = res?.data?.data ?? res?.data ?? res;
        setProfile(p);
        setForm({ firstName: p.firstName ?? '', lastName: p.lastName ?? '', phoneNumber: p.phoneNumber ?? '' });
      })
      .catch(() => setError('Could not load your profile.'));
  }, []);

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const res: any = await authApi.updateProfile({
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber,
      });
      const p = res?.data?.data ?? res?.data ?? res;
      setProfile(p);
      // Keep the header greeting in sync with the new name
      const stored = localStorage.getItem('user');
      if (stored) {
        const u = JSON.parse(stored);
        localStorage.setItem('user', JSON.stringify({ ...u, firstName: p.firstName, lastName: p.lastName, phoneNumber: p.phoneNumber }));
      }
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file: File) => {
    setPhotoError('');
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/users/${profile.id}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoVersion((v) => v + 1);
      const res: any = await authApi.getProfile();
      const p = res?.data?.data ?? res?.data ?? res;
      setProfile(p);
    } catch (e: any) {
      if (e?.response?.status === 400) {
        setPhotoError(e?.response?.data?.message || 'Invalid photo: use a PNG or JPEG under 10MB.');
      } else {
        setPhotoError(e?.response?.data?.message || 'Photo upload failed.');
      }
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const deletePhoto = async () => {
    setPhotoError('');
    setPhotoUploading(true);
    try {
      await api.delete(`/users/${profile.id}/photo`);
      const res: any = await authApi.getProfile();
      const p = res?.data?.data ?? res?.data ?? res;
      setProfile(p);
      setPhotoVersion((v) => v + 1);
    } catch (e: any) {
      setPhotoError(e?.response?.data?.message || 'Could not remove photo.');
    } finally {
      setPhotoUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
            <UserIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
            <p className="text-sm text-gray-500">Your personal information</p>
          </div>
        </div>

        {error && !profile && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

        {profile && (
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
            {saved && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Profile saved
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                <input
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                <input
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  value={form.phoneNumber}
                  onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                  disabled={!editing}
                  placeholder="Add a phone number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input value={profile.email} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
                <p className="text-xs text-gray-400 mt-1">
                  <a href="mailto:echobravotango@agentmail.to?subject=Conformara%20support%20-%20change%20email" className="underline hover:text-gray-600">Contact support</a> to change your email
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <input value={profile.role} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input value={profile.company?.name ?? ''} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
              </div>
            </div>

            {/* My badge (compact) + photo upload */}
            {profile.qrCode && (
              <div className="mt-6 border-t border-gray-100 pt-5" data-testid="profile-badge-section">
                <h2 className="font-semibold text-gray-900 text-sm mb-3">My badge</h2>
                <div className="flex flex-col sm:flex-row gap-5 items-start">
                  <CertBadge
                    name={`${profile.firstName} ${profile.lastName}`}
                    role={profile.role}
                    companyName={profile.company?.name}
                    /* Badge reaudit R2(c): set the cache-bust param with
                       URLSearchParams — the old template appended '?v=' to a
                       URL that already carried '?v=', producing a broken
                       double-'?' URL. CertBadge rewrites this to the public
                       QR-scoped photo route (R2b). */
                    photoUrl={
                      profile.photoUrl
                        ? (() => {
                            try {
                              const u = new URL(profile.photoUrl, window.location.origin);
                              u.searchParams.set('v', String(photoVersion));
                              return u.toString();
                            } catch {
                              return profile.photoUrl;
                            }
                          })()
                        : null
                    }
                    qrCode={profile.qrCode}
                    certs={profile.certifications ?? []}
                    variant="compact"
                  />
                  <div className="text-sm">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Badge photo (PNG or JPEG, under 10MB)</label>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      data-testid="photo-input"
                      disabled={photoUploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadPhoto(f);
                      }}
                      className="block text-xs text-gray-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:text-xs file:cursor-pointer disabled:opacity-50"
                    />
                    {photoUploading && <p className="mt-2 text-xs text-blue-600">Uploading…</p>}
                    {photoError && <p className="mt-2 text-xs text-red-600" data-testid="photo-error">{photoError}</p>}
                    {!photoError && !photoUploading && profile.photoUrl && (
                      <p className="mt-2 text-xs text-green-600">Photo saved.</p>
                    )}
                    {profile.photoUrl && (
                      <button
                        onClick={deletePhoto}
                        disabled={photoUploading}
                        className="mt-2 text-xs text-red-600 hover:text-red-700 underline disabled:opacity-50"
                      >
                        Remove photo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              {editing ? (
                <>
                  <button
                    onClick={save}
                    disabled={saving || !form.firstName.trim() || !form.lastName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    onClick={() => { setEditing(false); setForm({ firstName: profile.firstName ?? '', lastName: profile.lastName ?? '', phoneNumber: profile.phoneNumber ?? '' }); setError(''); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button onClick={() => setEditing(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Edit profile
                </button>
              )}
            </div>
          </div>
        )}

        <SignedDocumentsSection />
      </div>
    </div>
  );
}
